import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { addSceneProp } from '@platform/client';
import { ANIMAL_SOUNDS, castSlotSchema, isAnimal } from '@platform/schema/cast';
import { slugify } from '@platform/schema/generation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

import { castQuery } from './cast';
import { FieldWarnings } from './FieldWarnings';
import {
  draftIssues,
  partyOf,
  recast,
  respeak,
  seatFigure,
  withDisplay,
  withKey,
  withProps,
} from './scene-cast';

import type { Display, Figure, Line, Party } from './scene-cast';
import type { CastEntry, CastId, SceneProp } from '@platform/schema/cast';
import type { LessonCast } from '@platform/schema/content';
import type { SceneWarning } from '@platform/schema/scene-fix';

type Side = Line['by'];

const DEFAULT = '';
const MAX_PROPS = 3;
const MAX_LINES = 4;
const MAX_SEATED = 3;

const DISPLAYS: Record<Display | 'none', string> = {
  situation: 'Situation, with thought bubbles',
  dialogue: 'Speech and thought, two sides',
  none: 'No characters',
};

/** "lease-agreement" from "Lease agreement", the shape a prop slug takes. */
const slugOf = (label: string): string =>
  slugify(label).slice(0, 40).replace(/-+$/, '');

/** "Rosa, Owner": the registry's name for the character, then the role the lesson gives them. */
const memberLabel = (
  member: LessonCast[number],
  entryOf: (id: CastId) => CastEntry | undefined,
): string =>
  `${entryOf(member.character)?.name ?? member.character}, ${member.role}`;

/**
 * Characters & scene — who is in one scene and what they do, as the registry allows each
 * of them. Edits stay in a draft until Save, which writes the whole visual once: it is one
 * column, and a write per control would re-render the preview on every keystroke.
 */
export function CastInspector({
  saved,
  cast,
  headline,
  warnings,
  disabled,
  onSave,
}: {
  /** The figure the scene holds now, or `undefined` when it has none. */
  saved: Figure | undefined;
  cast: LessonCast;
  headline: string;
  warnings: readonly SceneWarning[];
  disabled: boolean;
  onSave: (visual: Figure | null) => void;
}) {
  const registry = useQuery(castQuery);
  const [draft, setDraft] = useState<Figure | null | undefined>(saved);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const issues =
    draft === null || draft === undefined ? [] : draftIssues(draft);

  const entryOf = (id: CastId | undefined): CastEntry | undefined =>
    registry.data?.cast.find((one) => one.id === id);
  const label = (party: Party): string => {
    const entry = entryOf(party.character);
    return entry === undefined ? party.name : `${entry.name}, ${party.name}`;
  };

  const display: Display | 'none' = draft?.kind ?? 'none';

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label="Characters & scene"
      sx={{ p: 2 }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle2" component="h3">
          Characters & scene
        </Typography>

        <FieldWarnings field="visual" warnings={warnings} disabled />

        {registry.isError && (
          <Alert severity="error">{describeApiError(registry.error)}</Alert>
        )}

        <TextField
          select
          label="Display"
          value={display}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value as Display | 'none';
            if (next === 'none')
              setDraft(saved === undefined ? undefined : null);
            else
              setDraft(withDisplay(draft ?? seatFigure(cast, headline), next));
          }}
        >
          {(Object.keys(DISPLAYS) as (Display | 'none')[]).map((key) => (
            <MenuItem key={key} value={key}>
              {DISPLAYS[key]}
            </MenuItem>
          ))}
        </TextField>

        {draft !== null &&
          draft !== undefined &&
          registry.data !== undefined && (
            <>
              <TextField
                label="Scene description — drawn under the picture and read aloud"
                value={draft.caption}
                disabled={disabled}
                onChange={(event) => {
                  setDraft({ ...draft, caption: event.target.value });
                }}
              />

              {draft.kind === 'dialogue' ? (
                <>
                  {(['left', 'right'] as const).map((side) => (
                    <PartyEditor
                      key={side}
                      heading={side === 'left' ? 'Left side' : 'Right side'}
                      party={draft[side]}
                      cast={cast}
                      entry={entryOf(draft[side].character)}
                      entryOf={entryOf}
                      disabled={disabled}
                      onChange={(party) => {
                        setDraft({
                          ...draft,
                          [side]: party,
                          lines: draft.lines.map((line) =>
                            line.by === side
                              ? respeak(line, party.character)
                              : line,
                          ),
                        });
                      }}
                    />
                  ))}
                  <Typography variant="subtitle2">Lines</Typography>
                  {draft.lines.map((line, index) => (
                    <LineEditor
                      // Lines have no id, and their order is the conversation's.
                      key={index}
                      index={index}
                      line={line}
                      speakers={{
                        left: label(draft.left),
                        right: label(draft.right),
                      }}
                      speaker={draft[line.by].character}
                      entry={entryOf(draft[line.by].character)}
                      disabled={disabled}
                      removable={draft.lines.length > 1}
                      onChange={(next) => {
                        setDraft({
                          ...draft,
                          lines: draft.lines.map((one, at) =>
                            at === index
                              ? respeak(next, draft[next.by].character)
                              : one,
                          ),
                        });
                      }}
                      onRemove={() => {
                        setDraft({
                          ...draft,
                          lines: draft.lines.filter((_one, at) => at !== index),
                        });
                      }}
                    />
                  ))}
                  <Button
                    disabled={disabled || draft.lines.length >= MAX_LINES}
                    onClick={() => {
                      setDraft({
                        ...draft,
                        lines: [...draft.lines, { by: 'left', text: '' }],
                      });
                    }}
                  >
                    Add line
                  </Button>
                </>
              ) : (
                <>
                  {draft.cast.map((party, index) => (
                    <PartyEditor
                      key={party.character ?? party.name}
                      heading={`Participant ${String(index + 1)}`}
                      party={party}
                      cast={cast}
                      entry={entryOf(party.character)}
                      entryOf={entryOf}
                      disabled={disabled}
                      thought
                      onChange={(next) => {
                        setDraft({
                          ...draft,
                          cast: draft.cast.map((one, at) =>
                            at === index ? next : one,
                          ),
                        });
                      }}
                      onRemove={
                        draft.cast.length > 1
                          ? () => {
                              setDraft({
                                ...draft,
                                cast: draft.cast.filter(
                                  (_one, at) => at !== index,
                                ),
                              });
                            }
                          : undefined
                      }
                    />
                  ))}
                  <AddParticipant
                    cast={cast}
                    seated={draft.cast}
                    entryOf={entryOf}
                    disabled={disabled || draft.cast.length >= MAX_SEATED}
                    onAdd={(member) => {
                      setDraft({
                        ...draft,
                        cast: [...draft.cast, partyOf(member)],
                      });
                    }}
                  />
                </>
              )}

              <PropPicker
                props={registry.data.props}
                chosen={draft.props ?? []}
                disabled={disabled}
                onChange={(slugs) => {
                  setDraft(withProps(draft, slugs));
                }}
              />
            </>
          )}

        {issues.length > 0 && (
          <Alert severity="warning" data-testid="draft-issues">
            Not saved yet:
            <ul>
              {issues.map((issue) => (
                <li key={`${issue.at}${issue.message}`}>
                  {issue.at === '' ? '' : `${issue.at}: `}
                  {issue.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={disabled || !dirty || issues.length > 0}
            onClick={() => {
              onSave(draft ?? null);
            }}
          >
            Save characters & scene
          </Button>
          <Button
            disabled={disabled || !dirty}
            onClick={() => {
              setDraft(saved);
            }}
          >
            Discard changes
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** One option per registry value, after the default that omits the field. */
function OptionalSelect<T extends string>({
  label,
  value,
  options,
  none,
  disabled,
  onChange,
}: {
  label: string;
  value: T | undefined;
  options: readonly T[];
  none: string;
  disabled: boolean;
  onChange: (value: T | undefined) => void;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value ?? DEFAULT}
      disabled={disabled}
      sx={{ minWidth: 140 }}
      onChange={(event) => {
        const next = event.target.value;
        onChange(next === DEFAULT ? undefined : (next as T));
      }}
    >
      <MenuItem value={DEFAULT}>{none}</MenuItem>
      {options.map((one) => (
        <MenuItem key={one} value={one}>
          {one}
        </MenuItem>
      ))}
    </TextField>
  );
}

function PartyEditor({
  heading,
  party,
  cast,
  entry,
  entryOf,
  disabled,
  thought = false,
  onChange,
  onRemove,
}: {
  heading: string;
  party: Party;
  cast: LessonCast;
  entry: CastEntry | undefined;
  entryOf: (id: CastId | undefined) => CastEntry | undefined;
  disabled: boolean;
  thought?: boolean;
  onChange: (party: Party) => void;
  onRemove?: (() => void) | undefined;
}) {
  return (
    <Stack
      spacing={1}
      role="group"
      aria-label={heading}
      sx={{ borderLeft: 2, borderColor: 'divider', pl: 2 }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {heading}
        </Typography>
        {onRemove !== undefined && (
          <Button size="small" disabled={disabled} onClick={onRemove}>
            Remove from this scene
          </Button>
        )}
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="Who"
          value={party.character ?? DEFAULT}
          disabled={disabled}
          sx={{ minWidth: 180 }}
          onChange={(event) => {
            const member = cast.find(
              (one) => one.character === event.target.value,
            );
            const next =
              member === undefined ? undefined : entryOf(member.character);
            if (member !== undefined && next !== undefined)
              onChange(recast(party, member, next));
          }}
        >
          {party.character === undefined && (
            <MenuItem value={DEFAULT}>{party.name}</MenuItem>
          )}
          {cast.map((member) => (
            <MenuItem key={member.character} value={member.character}>
              {memberLabel(member, entryOf)}
            </MenuItem>
          ))}
        </TextField>
        {entry !== undefined && (
          <>
            <OptionalSelect
              label="Pose"
              value={party.pose}
              options={entry.poses}
              none="Default"
              disabled={disabled}
              onChange={(pose) => {
                onChange(withKey(party, 'pose', pose));
              }}
            />
            <OptionalSelect
              label="Facing"
              value={party.facing}
              options={entry.facings}
              none="As drawn"
              disabled={disabled}
              onChange={(facing) => {
                onChange(withKey(party, 'facing', facing));
              }}
            />
            <OptionalSelect
              label="Reaction after the answer"
              value={party.reaction}
              options={entry.reactions}
              none="None"
              disabled={disabled}
              onChange={(reaction) => {
                onChange(withKey(party, 'reaction', reaction));
              }}
            />
          </>
        )}
        <OptionalSelect
          label="Position"
          value={party.slot}
          options={castSlotSchema.options}
          none="Automatic"
          disabled={disabled}
          onChange={(slot) => {
            onChange(withKey(party, 'slot', slot));
          }}
        />
      </Stack>
      {thought && (
        <TextField
          size="small"
          label="Thought bubble"
          value={party.thought ?? ''}
          disabled={disabled}
          onChange={(event) => {
            const text = event.target.value;
            onChange(withKey(party, 'thought', text === '' ? undefined : text));
          }}
        />
      )}
    </Stack>
  );
}

function LineEditor({
  index,
  line,
  speakers,
  speaker,
  entry,
  disabled,
  removable,
  onChange,
  onRemove,
}: {
  index: number;
  line: Line;
  speakers: Record<Side, string>;
  speaker: CastId | undefined;
  entry: CastEntry | undefined;
  disabled: boolean;
  removable: boolean;
  onChange: (line: Line) => void;
  onRemove: () => void;
}) {
  const animal = speaker !== undefined && isAnimal(speaker);
  const moods = entry?.moods ?? [];

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      role="group"
      aria-label={`Line ${String(index + 1)}`}
      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
    >
      <TextField
        select
        size="small"
        label="Speaker"
        value={line.by}
        disabled={disabled}
        sx={{ minWidth: 160 }}
        onChange={(event) => {
          onChange({ ...line, by: event.target.value as Side });
        }}
      >
        {(['left', 'right'] as const).map((side) => (
          <MenuItem key={side} value={side}>
            {speakers[side]}
          </MenuItem>
        ))}
      </TextField>
      {animal ? (
        <OptionalSelect
          label="Sound"
          value={line.sound}
          options={ANIMAL_SOUNDS[speaker]}
          none="Choose a sound"
          disabled={disabled}
          onChange={(sound) => {
            if (sound !== undefined) onChange({ ...line, sound });
          }}
        />
      ) : (
        <>
          <TextField
            size="small"
            label={`Line ${String(index + 1)}`}
            value={line.text ?? ''}
            disabled={disabled}
            sx={{ flex: 1, minWidth: 200 }}
            onChange={(event) => {
              onChange({ ...line, text: event.target.value });
            }}
          />
          <FormControlLabel
            label="Thought"
            control={
              <Switch
                checked={line.thought === true}
                disabled={disabled}
                onChange={(_event, on) => {
                  onChange(withKey(line, 'thought', on ? true : undefined));
                }}
              />
            }
          />
        </>
      )}
      <OptionalSelect
        label="Mood"
        value={line.mood}
        options={moods}
        none="Automatic"
        disabled={disabled}
        onChange={(mood) => {
          onChange(withKey(line, 'mood', mood));
        }}
      />
      {removable && (
        <Button size="small" disabled={disabled} onClick={onRemove}>
          Remove line
        </Button>
      )}
    </Stack>
  );
}

function AddParticipant({
  cast,
  seated,
  entryOf,
  disabled,
  onAdd,
}: {
  cast: LessonCast;
  seated: readonly Party[];
  entryOf: (id: CastId | undefined) => CastEntry | undefined;
  disabled: boolean;
  onAdd: (member: LessonCast[number]) => void;
}) {
  const free = cast.filter(
    (member) => !seated.some((one) => one.character === member.character),
  );
  if (free.length === 0) return null;

  return (
    <TextField
      select
      size="small"
      label="Add a participant from the lesson's cast"
      value={DEFAULT}
      disabled={disabled}
      onChange={(event) => {
        const member = free.find((one) => one.character === event.target.value);
        if (member !== undefined) onAdd(member);
      }}
    >
      {free.map((member) => (
        <MenuItem key={member.character} value={member.character}>
          {memberLabel(member, entryOf)}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Searches the props authors have added, and adds one — CAST-11's endpoint. */
function PropPicker({
  props,
  chosen,
  disabled,
  onChange,
}: {
  props: readonly SceneProp[];
  chosen: readonly string[];
  disabled: boolean;
  onChange: (slugs: string[]) => void;
}) {
  const queries = useQueryClient();
  const [naming, setNaming] = useState<string>();

  const add = useTrackedMutation('Add a prop', {
    mutationFn: async (label: string) =>
      (
        await addSceneProp({
          body: { slug: slugOf(label), label },
          throwOnError: true,
        })
      ).data,
    onSuccess: async (prop) => {
      await queries.invalidateQueries({ queryKey: castQuery.queryKey });
      onChange([...chosen, prop.slug]);
      setNaming(undefined);
    },
  });

  const full = chosen.length >= MAX_PROPS;
  const labelOf = (slug: string): string =>
    props.find((one) => one.slug === slug)?.label ?? slug;

  return (
    <Stack spacing={1}>
      <Autocomplete
        multiple
        size="small"
        options={props.map((one) => one.slug)}
        value={[...chosen]}
        getOptionLabel={labelOf}
        getOptionDisabled={() => full}
        disabled={disabled}
        onChange={(_event, slugs) => {
          onChange(slugs.slice(0, MAX_PROPS));
        }}
        renderInput={(params) => (
          <TextField {...params} label={`Props — up to ${String(MAX_PROPS)}`} />
        )}
      />
      {naming === undefined ? (
        <Button
          size="small"
          sx={{ alignSelf: 'flex-start' }}
          disabled={disabled || full}
          onClick={() => {
            setNaming('');
          }}
        >
          Add prop
        </Button>
      ) : (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            size="small"
            label="New prop"
            value={naming}
            helperText={
              slugOf(naming) === ''
                ? 'Name it the way an author would look for it.'
                : `Saved as ${slugOf(naming)}. It shows as a labelled placeholder until it is drawn.`
            }
            onChange={(event) => {
              setNaming(event.target.value);
            }}
          />
          <Button
            disabled={slugOf(naming) === '' || add.isPending}
            onClick={() => {
              add.mutate(naming.trim());
            }}
          >
            Add
          </Button>
          <Button
            onClick={() => {
              setNaming(undefined);
              add.reset();
            }}
          >
            Cancel
          </Button>
        </Stack>
      )}
      {add.isError && (
        <Alert severity="error">{describeApiError(add.error)}</Alert>
      )}
    </Stack>
  );
}
